import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, MapPin } from "lucide-react";
import { site } from "@/config/site";
import { collections } from "@/data/products";
import { AREAS } from "@/data/areas";
import { reviews, steps } from "@/data/content";
import { useLeadForm } from "@/components/lead/LeadForm";
import { SEO, breadcrumbLd, faqLd, localBusinessLd } from "@/components/site/SEO";
import { Reveal, Eyebrow } from "@/components/site/Reveal";
import { ProductCard } from "@/components/site/ProductCard";
import { FAQ } from "@/components/site/FAQ";
import { CTASection } from "@/components/site/CTASection";
import { Star } from "lucide-react";

const faqs = [
  {
    q: "Do you cover all of Birmingham?",
    a: "Yes — from the city centre out to Harborne, Edgbaston, Erdington, Kings Heath, Yardley and everywhere in between, plus the wider West Midlands. If you're unsure, send your postcode and we'll confirm straight away.",
  },
  {
    q: "Is measuring and quoting free in Birmingham?",
    a: "Yes — the home visit, professional measuring and written quotation are free and without obligation everywhere we cover.",
  },
  {
    q: "Which blinds are most popular in Birmingham homes?",
    a: "Roller and Perfect Fit blinds lead for modern homes and apartments, while wooden venetians and shutters are the favourites for the city's bay-windowed terraces and semis.",
  },
  {
    q: "Do you fit blinds for landlords and businesses in Birmingham?",
    a: "Yes — we work with homeowners, landlords, letting agents and businesses. See our commercial blinds page for offices, shops and other premises.",
  },
];

export default function BlindsBirmingham() {
  const { openForm } = useLeadForm();
  return (
    <>
      <SEO
        title="Blinds Birmingham | Made-to-Measure, Measured & Fitted | My Blinds Company"
        description="Made-to-measure blinds in Birmingham for homes, landlords and businesses. Free home measuring, clear quotes and professional fitting across the city. Book today."
        path="/blinds-birmingham"
        jsonLd={[
          localBusinessLd,
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: "Blinds Birmingham", path: "/blinds-birmingham" },
          ]),
          faqLd(faqs),
        ]}
      />

      {/* HERO */}
      <section className="relative bg-navy-950 text-white overflow-hidden grain" data-testid="brum-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
          <Reveal className="max-w-3xl">
            <p className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 backdrop-blur px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-white mb-6">
              <MapPin className="h-3.5 w-3.5" /> Birmingham & the West Midlands
            </p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.08]">
              Made-to-Measure Blinds in Birmingham
            </h1>
            <p className="mt-6 text-base sm:text-lg leading-relaxed text-white/75 max-w-2xl">
              Looking for professionally measured and fitted blinds in Birmingham?
              My Blinds Company supplies made-to-measure blinds and shutters for
              homes, landlords and businesses throughout Birmingham and the
              surrounding areas.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                data-testid="brum-book-btn"
                onClick={() => openForm({ source: "blinds_birmingham" })}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-8 py-4 text-sm font-semibold text-navy-900 shadow-2xl transition-all duration-300 hover:-translate-y-0.5"
              >
                Book My Free Home Measure <ArrowRight className="h-4 w-4" />
              </button>
              <Link
                to="/free-home-measure"
                data-testid="brum-measure-link"
                className="inline-flex items-center justify-center rounded-full border border-white/40 bg-white/5 backdrop-blur px-8 py-4 text-sm font-semibold text-white transition-all duration-300 hover:bg-white hover:text-navy-900"
              >
                How the Free Measure Works
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* PRODUCT CATEGORIES */}
      <section className="py-16 sm:py-24" data-testid="brum-products">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="max-w-2xl mb-12">
            <Eyebrow>What We Make & Fit</Eyebrow>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl text-slate-900 tracking-tight">
              Every blind style, measured for your windows
            </h2>
            <p className="mt-4 text-base leading-relaxed text-slate-600">
              Roller, roman, venetian, wooden venetian, vertical, day &amp; night,
              Perfect Fit, motorised, skylight and blackout blinds — plus
              made-to-measure plantation shutters. All measured and fitted by our
              own Birmingham-based specialists.
            </p>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {collections.slice(0, 8).map((p, i) => (
              <Reveal key={p.slug} delay={(i % 4) * 0.06}>
                <ProductCard product={p} className="h-full" />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="py-16 sm:py-24 bg-warm-100/60" data-testid="brum-process">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="max-w-2xl mb-12">
            <Eyebrow>Free Measuring Process</Eyebrow>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl text-slate-900 tracking-tight">
              From first visit to final fitting
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
            {steps.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.08}>
                <div>
                  <span className="font-serif italic text-6xl text-warm-300 select-none">{s.n}</span>
                  <h3 className="mt-3 font-sans text-lg font-semibold text-slate-900">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* REVIEWS SNAPSHOT */}
      <section className="py-16 sm:py-24" data-testid="brum-reviews">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="mb-10 text-center">
            <Eyebrow className="justify-center">What Our Customers Say</Eyebrow>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl text-slate-900 tracking-tight">
              Trusted by Birmingham customers
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            {reviews.map((r, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <figure className="h-full rounded-2xl border border-warm-200 bg-white p-7 shadow-sm">
                  <div className="flex gap-1 mb-4" aria-label={`${r.rating} out of 5 stars`}>
                    {[...Array(r.rating || 5)].map((_, s) => (
                      <Star key={s} className="h-4 w-4 fill-[#C9A227] text-[#C9A227]" />
                    ))}
                  </div>
                  <blockquote className="text-sm leading-relaxed text-slate-700">“{r.text}”</blockquote>
                  <figcaption className="mt-4 text-xs font-semibold uppercase tracking-wider text-slate-500">
                    {r.name} <span className="font-normal normal-case text-slate-400">· {r.source}</span>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
          {site.social.googleReviews && (
            <Reveal className="mt-8 flex flex-wrap justify-center gap-3">
              <a
                href={site.social.googleReviews}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="brum-reviews-link"
                className="inline-flex items-center gap-2 rounded-full border border-warm-300 px-6 py-3 text-sm font-semibold text-navy-900 hover:border-navy-800/40 transition-colors"
              >
                Read Our Google Reviews <ArrowRight className="h-4 w-4" />
              </a>
              <a
                href={site.social.googleReviews}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="brum-leave-review-link"
                className="inline-flex items-center gap-2 rounded-full border border-warm-300 px-6 py-3 text-sm font-semibold text-navy-900 hover:border-navy-800/40 transition-colors"
              >
                Leave Us a Google Review <ArrowRight className="h-4 w-4" />
              </a>
            </Reveal>
          )}
        </div>
      </section>

      {/* AREAS */}
      <section className="py-16 sm:py-24 bg-navy-950 grain text-white" data-testid="brum-areas">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="max-w-2xl mb-10">
            <Eyebrow className="text-white/60">Areas Covered</Eyebrow>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl tracking-tight">
              Across Birmingham & the West Midlands
            </h2>
            <p className="mt-4 text-base leading-relaxed text-white/70">
              We measure and fit throughout the city and surrounding towns —
              including Harborne, Edgbaston, Erdington, Kings Heath, Yardley,
              Small Heath and beyond.
            </p>
          </Reveal>
          <div className="flex flex-wrap gap-3">
            {AREAS.map((a) => (
              <Link
                key={a.slug}
                to={`/areas/${a.slug}`}
                data-testid={`brum-area-${a.slug}`}
                className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-5 py-2.5 text-sm font-semibold text-white hover:bg-white hover:text-navy-900 transition-colors"
              >
                <MapPin className="h-3.5 w-3.5" /> Blinds in {a.name}
              </Link>
            ))}
          </div>
          <Reveal className="mt-10">
            <ul className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {["Free home measuring & quote", "Homes, landlords & businesses", "Professional fitting included", "Local Birmingham team"].map((t) => (
                <li key={t} className="flex items-start gap-2.5 text-sm text-white/80">
                  <CheckCircle2 className="h-4 w-4 text-warm-200 mt-0.5 shrink-0" /> {t}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 sm:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-10">
            <Eyebrow className="justify-center">Questions</Eyebrow>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl text-slate-900 tracking-tight">
              Blinds in Birmingham — FAQs
            </h2>
          </Reveal>
          <FAQ items={faqs} idPrefix="brum-faq" />
        </div>
      </section>

      <CTASection source="blinds_birmingham_final" />
    </>
  );
}
